import { useState } from "react";

export interface FAQ {
  q: string;
  a: string;
}

interface FAQSectionProps {
  title?: string;
  subtitle?: string;
  faqs: FAQ[];
}

export default function FAQSection({
  title = "Frequently Asked Questions",
  subtitle = "Answers to what Sarasota homeowners ask us most — including voice search questions.",
  faqs,
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Left sticky label */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-4 mb-5">
              <div className="h-px w-10 bg-aegisGold" />
              <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">FAQs</span>
            </div>
            <h2 className="font-cormorant font-semibold text-aegisDark leading-[1.1] mb-5" style={{ fontSize: "clamp(1.8rem, 2.8vw, 2.8rem)" }}>
              {title}
            </h2>
            <div className="w-12 h-0.5 bg-aegisGold mb-6" />
            <p className="font-inter text-aegisMuted text-sm leading-relaxed mb-10">
              {subtitle}
            </p>

            {/* Voice search indicator */}
            <div className="flex items-center gap-3 mb-8 bg-aegisCream px-5 py-4 border-l-2 border-aegisGold">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-mic-line text-aegisGold text-base" />
              </div>
              <p className="font-inter text-aegisMuted text-xs leading-relaxed">
                Voice-search optimized answers for &ldquo;near me&rdquo; queries across Sarasota.
              </p>
            </div>

            {/* Phone CTA box */}
            <div className="bg-aegisDark p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-aegisGold text-sm" />
                </div>
                <p className="font-inter text-xs tracking-widest uppercase text-aegisGold">Still Have Questions?</p>
              </div>
              <p className="font-inter text-white/55 text-sm mb-6 leading-relaxed">
                Speak directly with our team. We respond within 24 hours.
              </p>
              <a
                href="tel:9414870007"
                className="inline-flex items-center gap-3 bg-aegisGold text-white font-inter font-semibold text-sm tracking-wide uppercase px-6 py-3 whitespace-nowrap cursor-pointer hover:bg-amber-700 transition-colors duration-200"
              >
                <i className="ri-phone-line" />
                (941) 487-0007
              </a>
            </div>
          </div>

          {/* Right accordion */}
          <div className="lg:col-span-2">
            <div className="divide-y divide-aegisStone">
              {faqs.map((faq, i) => {
                const isOpen = openIndex === i;
                return (
                  <div key={i} className={`transition-all duration-200 ${isOpen ? "bg-aegisCream -mx-2 px-2" : ""}`}>
                    <button
                      className="w-full flex items-start justify-between gap-5 py-7 text-left cursor-pointer group"
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-start gap-5 flex-1">
                        <span className="font-cormorant font-semibold text-aegisGold/40 text-xl leading-tight flex-shrink-0 mt-px">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className={`font-inter font-medium text-base leading-snug transition-colors duration-200 ${isOpen ? "text-aegisDark" : "text-aegisText group-hover:text-aegisGold"}`}>
                          {faq.q}
                        </h3>
                      </div>
                      <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 border transition-colors duration-200 ${isOpen ? "border-aegisGold bg-aegisGold" : "border-aegisStone group-hover:border-aegisGold"}`}>
                        <i className={`text-sm transition-all duration-200 ${isOpen ? "ri-subtract-line text-white" : "ri-add-line text-aegisMuted group-hover:text-aegisGold"}`} />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="pb-7 pl-14 pr-4">
                        <div className="border-l-2 border-aegisGold pl-5">
                          <p className="font-inter text-aegisMuted text-sm leading-[1.8]">
                            {faq.a}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
